import {Component, EventEmitter, Input, Output, OnChanges, SimpleChanges} from '@angular/core';
import {CamposMesclagem, CorrespondenciasVinculacaoProdutoCanalVendaVM, EscolhasCamposMesclagem,
  PreviaMesclagemVinculacaoVM} from './canais-venda.models';

type GrupoLocal = PreviaMesclagemVinculacaoVM['gruposLocais'][number];
type GrupoExterno = PreviaMesclagemVinculacaoVM['gruposExternos'][number];
type OpcaoExterna = GrupoExterno['opcoes'][number];

@Component({
  selector: 'app-mesclagem-produto-canal-venda',
  templateUrl: './mesclagem-produto-canal-venda.component.html',
  styleUrls: ['./mesclagem-produto-canal-venda.component.scss']
})
export class MesclagemProdutoCanalVendaComponent implements OnChanges {
  @Input() nomePlataforma = 'iFood';
  @Input() salvando = false;
  @Input() mostrarVoltar = true;
  @Input() erro = '';
  @Input() previa: PreviaMesclagemVinculacaoVM | null = null;
  @Input() revisao: CorrespondenciasVinculacaoProdutoCanalVendaVM | null = null;
  @Output() cancelar = new EventEmitter<void>();
  @Output() confirmar = new EventEmitter<CorrespondenciasVinculacaoProdutoCanalVendaVM>();

  dados: PreviaMesclagemVinculacaoVM | null = null;
  gruposSelecionados: {[id: string]: number | null} = {};
  opcoesSelecionadas: {[id: string]: number | null} = {};
  camposProduto: EscolhasCamposMesclagem = {};
  camposOpcao: {[id: string]: EscolhasCamposMesclagem} = {};
  gruposRecolhidos: {[id: string]: boolean} = {};

  ngOnChanges(changes?: SimpleChanges): void {
    if(changes && !changes.previa && !changes.revisao) return;
    this.dados = this.previa;
    this.gruposSelecionados = {}; this.opcoesSelecionadas = {};
    this.camposProduto = {}; this.camposOpcao = {}; this.gruposRecolhidos = {};
    if(!this.dados) return;
    for(const grupo of this.dados.gruposExternos)
      this.selecioneGrupo(grupo, this.analiseGrupo(grupo)?.localId ?? null);
    if(this.revisao?.versao !== this.dados.versao) return;
    this.camposProduto = {...this.revisao.camposProduto};
    for(const grupo of this.dados.gruposExternos) {
      const salvo = this.revisao.grupos.find(atual => atual.externalId === grupo.id);
      if(salvo) this.selecioneGrupo(grupo, salvo.localId);
      for(const opcao of grupo.opcoes) {
        const salva = this.revisao.opcoes.find(atual => atual.externalId === opcao.id);
        if(salva) {
          this.opcoesSelecionadas[opcao.id] = salva.localId;
          this.camposOpcao[opcao.id] = {...salva.campos};
        }
      }
    }
  }

  analiseGrupo(grupo: GrupoExterno) {
    return this.dados?.analise.grupos.find(atual => atual.externalId === grupo.id);
  }
  analiseOpcao(grupo: GrupoExterno, opcao: OpcaoExterna) {
    return this.analiseGrupo(grupo)?.comparacoes
      .find(atual => atual.localId === this.gruposSelecionados[grupo.id])?.opcoes
      .find(atual => atual.externalId === opcao.id);
  }
  grupoLocal(grupo: GrupoExterno): GrupoLocal | null {
    return this.dados?.gruposLocais.find(local => local.id === this.gruposSelecionados[grupo.id]) || null;
  }
  opcaoLocal(grupo: GrupoExterno, opcao: OpcaoExterna): GrupoLocal['opcoes'][number] | null {
    return this.grupoLocal(grupo)?.opcoes.find(local => local.id === this.opcoesSelecionadas[opcao.id]) || null;
  }
  grupoExternoParaLocal(local: GrupoLocal): GrupoExterno | null {
    return this.dados?.gruposExternos.find(grupo => this.gruposSelecionados[grupo.id] === local.id) || null;
  }
  gruposExternosDisponiveis(local: GrupoLocal): GrupoExterno[] {
    return (this.dados?.gruposExternos || []).filter(grupo =>
      this.gruposSelecionados[grupo.id] == null || this.gruposSelecionados[grupo.id] === local.id);
  }
  selecioneGrupoParaLocal(local: GrupoLocal, externalId: string | null): void {
    const anterior = this.grupoExternoParaLocal(local);
    if(anterior && anterior.id !== externalId) this.selecioneGrupo(anterior, null);
    const grupo = this.dados?.gruposExternos.find(item => item.id === externalId);
    if(grupo) this.selecioneGrupo(grupo, local.id);
  }
  gruposExternosSemCorrespondencia(): GrupoExterno[] {
    return (this.dados?.gruposExternos || []).filter(grupo => this.gruposSelecionados[grupo.id] == null);
  }
  opcaoExternaParaLocal(grupo: GrupoExterno | null, localId: number): OpcaoExterna | null {
    return grupo?.opcoes.find(opcao => this.opcoesSelecionadas[opcao.id] === localId) || null;
  }
  opcoesExternasDisponiveis(grupo: GrupoExterno | null, localId: number): OpcaoExterna[] {
    return (grupo?.opcoes || []).filter(opcao =>
      this.opcoesSelecionadas[opcao.id] == null || this.opcoesSelecionadas[opcao.id] === localId);
  }
  selecioneOpcaoParaLocal(grupo: GrupoExterno | null, localId: number, externalId: string | null): void {
    if(!grupo) return;
    const anterior = this.opcaoExternaParaLocal(grupo, localId);
    if(anterior && anterior.id !== externalId) this.selecioneOpcao(anterior, null);
    const opcao = grupo.opcoes.find(item => item.id === externalId);
    if(opcao) this.selecioneOpcao(opcao, localId);
  }
  opcoesExternasSemCorrespondencia(grupo: GrupoExterno | null): OpcaoExterna[] {
    return (grupo?.opcoes || []).filter(opcao => this.opcoesSelecionadas[opcao.id] == null);
  }
  precoExibicao(campos: CamposMesclagem): number | null {
    const preco = campos?.preco;
    return typeof preco === 'number' && Number.isFinite(preco) ? preco : null;
  }
  selecioneGrupo(grupo: GrupoExterno, localId: number | null): void {
    this.gruposSelecionados[grupo.id] = localId;
    for(const opcao of grupo.opcoes) this.selecioneOpcao(opcao,
      localId == null ? null : this.analiseOpcao(grupo, opcao)?.localId ?? null);
  }
  selecioneOpcao(opcao: OpcaoExterna, localId: number | null): void {
    this.opcoesSelecionadas[opcao.id] = localId;
    this.camposOpcao[opcao.id] = {};
  }
  private escolhaPdvValida(local: string, remoto: string, escolhas: EscolhasCamposMesclagem): boolean {
    if((local || '') === (remoto || '')) return !escolhas?.codigoPdv;
    const escolha = escolhas?.codigoPdv;
    return escolha === 'LOCAL' && !!local || escolha === 'IFOOD' && !!remoto;
  }
  podeConfirmar(): boolean {
    if(!this.dados || this.salvando || !this.escolhaPdvValida(
      this.dados.produto.local.codigoPdv, this.dados.produto.externo.codigoPdv, this.camposProduto)) return false;
    const grupos = new Set<number>(), opcoes = new Set<number>();
    for(const remoto of this.dados.gruposExternos) {
      const local = this.grupoLocal(remoto);
      if(!local) continue;
      if(grupos.has(local.id)) return false;
      grupos.add(local.id);
      for(const opcao of remoto.opcoes) {
        const localOpcao = this.opcaoLocal(remoto, opcao);
        if(!localOpcao) continue;
        if(opcoes.has(localOpcao.id) || !this.escolhaPdvValida(
          localOpcao.codigoPdv, opcao.codigoPdv, this.camposOpcao[opcao.id])) return false;
        opcoes.add(localOpcao.id);
      }
    }
    return true;
  }
  confirme(): void {
    if(!this.podeConfirmar() || !this.dados) return;
    const escolhas = this.obtenhaEscolhas();
    if(escolhas) this.confirmar.emit(escolhas);
  }
  obtenhaEscolhas(): CorrespondenciasVinculacaoProdutoCanalVendaVM | null {
    if(!this.dados) return null;
    return {versao: this.dados.versao, camposProduto: {...this.camposProduto},
      manterLocaisSemCorrespondencia: false,
      grupos: this.dados.gruposExternos.map(grupo => ({externalId: grupo.id,
        localId: this.gruposSelecionados[grupo.id], criar: false, campos: {}})),
      opcoes: this.dados.gruposExternos.reduce((todas, grupo) => todas.concat(grupo.opcoes.map(opcao => ({
        externalId: opcao.id, localId: this.opcoesSelecionadas[opcao.id], criar: false,
        campos: {...this.camposOpcao[opcao.id]}
      }))), [] as CorrespondenciasVinculacaoProdutoCanalVendaVM['opcoes'])};
  }
}
